import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from 'src/firebase/firebaseConfig';

const MUSICKIT_JS_URL = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

/**
 * Lazy-loads MusicKit JS, configures it with a developer token from our
 * backend, and exposes authorize / unauthorize helpers.
 *
 * The hook is intentionally stateless across components — MusicKit itself is a
 * singleton (MusicKit.getInstance()), so multiple consumers share one session.
 */
export function useAppleMusicAuth() {
  const [isReady, setIsReady] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initStartedRef = useRef(false);

  // ---- Bootstrap MusicKit JS ----
  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);

        // 1. Fetch developer token from our function
        const getToken = httpsCallable<Record<string, never>, { success: boolean; token: string }>(
          functions,
          'getAppleMusicToken'
        );
        const { data } = await getToken({});
        if (!data.success || !data.token) throw new Error('No developer token returned');
        if (cancelled) return;

        // 2. Load MusicKit JS if not already present
        if (!window.MusicKit) {
          await loadScript(MUSICKIT_JS_URL);
        }
        if (cancelled) return;

        // 3. Configure MusicKit
        const instance = await window.MusicKit.configure({
          developerToken: data.token,
          app: { name: 'Jarrell', build: '1.0.0' },
        });

        // Sync initial auth state
        setIsAuthorized(instance.isAuthorized);

        // Listen for auth changes
        instance.addEventListener('authorizationStatusDidChange', () => {
          setIsAuthorized(instance.isAuthorized);
        });

        setIsReady(true);
      } catch (err: any) {
        if (!cancelled) {
          console.error('MusicKit init error:', err);
          setError(err?.message || 'Failed to initialise Apple Music');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Authorize (sign in) ----
  const authorize = useCallback(async () => {
    if (!isReady) return;
    try {
      setIsLoading(true);
      const music = window.MusicKit.getInstance();
      await music.authorize();
      setIsAuthorized(music.isAuthorized);
    } catch (err: any) {
      console.error('Apple Music authorize error:', err);
      setError(err?.message || 'Authorization failed');
    } finally {
      setIsLoading(false);
    }
  }, [isReady]);

  // ---- Unauthorize (sign out) ----
  const unauthorize = useCallback(async () => {
    if (!isReady) return;
    try {
      const music = window.MusicKit.getInstance();
      await music.unauthorize();
      setIsAuthorized(false);
    } catch (err: any) {
      console.error('Apple Music unauthorize error:', err);
    }
  }, [isReady]);

  return { isReady, isAuthorized, isLoading, error, authorize, unauthorize };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Dynamically inject a <script> tag and wait for it to load. */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Don't double-load
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}
